/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Box, Chip, Divider, FormControl, FormControlLabel, Grid, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import { fonts, themes } from '../../../common/themes/themes';
import './Complaints.css';
import { useAppSelector } from '../../../infra/hooks';
import { complaintService } from '../../../api/services/complaintService';
import moment from 'moment';
import _ from 'lodash';
import CustomAddButton from '../../../common/components/CustomAddButton/CustomAddButton';
import CustomModal from '../../../common/Modals/CustomModal/CustomModal';
import ModalHeader from '../../../common/Modals/CustomModal/ModalHeader';
import SearchComponent from '../../../common/components/SearchComponent';
import { InfinityList } from '../../../components/list';
import { Loader } from '../../../components/icons/Loader';
import { DatePickerInput } from '../../../components';
import { defaultDateFormatting } from '../../../common/helper';
import CustomTextField from '../../../common/components/CustomTextField/CustomTextField';
import ModalFooter from '../../../common/Modals/CustomModal/ModalFooter';

interface propComplaints {
  trans?: any;
  i18n?: any;
}
const styles = {
  name: {
    color: '#464545',
    fontSize: 14,
  },
  designation: {
    color: '#8181A5',
    fontSize: 10,
  },
  radio: {
    color: '#7C0E6F',
    '&.Mui-checked': {
      color: '#7C0E6F',
    },
  },
  save: {
    boxShadow: 'none',
    textTransform: 'capitalize',
    fontSize: 14,
    color: 'white',
    padding: '6px 12px',
    border: '1px solid',
    lineHeight: 1.5,
    backgroundColor: '#7C0E6F',

    borderRadius: '0.5em',
    height: '42px',
    width: '120px',
    '&:hover': {
      backgroundColor: '#7C0E6F',
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor: '#7C0E6F',
    },
    '&:disabled': {
      backgroundColor: '#f5f5f5',
    },
    '&:focus': {
      boxShadow: 'none',
    },
  },
};

const Employee = (props: any) => {
  const [, setDesignation] = useState('');
  useEffect(() => {
    if (props.job) {
      if (props.job.designation) {
        setDesignation(props.job.designation.name);
      }
    }
  }, []);

  return (
    <Stack sx={{ padding: 1, marginBottom: 1 }}>
      <Typography style={styles.name}>
        {props.fname + ' ' + props.lname}
      </Typography>
      <Typography style={styles.designation}>
        {props?.job?.designation?.name}
      </Typography>
      <Typography style={styles.designation}>{props.dept}</Typography>
      <Typography style={styles.designation}>{props.staffid}</Typography>
    </Stack>
  );
};

const ComplaintsPresenter = ({ trans, i18n }: propComplaints) => {
  const isHRView = useAppSelector((state) => state.authSlice);
  const [fromDate, setFromDate] = useState(moment().subtract(1, 'year').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(moment().add(1, 'year').format('YYYY-MM-DD'));
  const [complaintsData, setComplaitsData] = useState([]);
  const [openModal, closeModal] = useState(false);

  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [rowPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debounceTerm, setDebounceTerm] = useState('');
  const [employeeData, setEmployeeData] = useState<any>([]);
  const [count, setCount] = useState(0);
  const [load, setLoad] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(undefined);
  const [date, setDate] = useState('');

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const getComplaints = () => {
    complaintService
      .getComplaintListByEmp(isHRView?.emp_id, fromDate, toDate)
      .then((res: any) => {
        console.log(res)
        setComplaitsData(res?.data?.data)
      })
  }

  const getCompanyEmployees = () => {
    complaintService
      .getCompanyEmployees(isHRView?.company_id, page, rowPerPage, searchTerm)
      .then((res: any) => {
        if (page === 1) setEmployeeData(res?.data);
        else {
          setEmployeeData((prevData: any) => [
            ...prevData,
            ...(Array.isArray(res?.data) ? res.data : [])
          ]);
        }
        setLastPage(res?.last_page)
        setCount(res?.total)
        setLoad(false)
      })
  }

  useEffect(() => {
    getComplaints();
  }, []);

  useEffect(() => {
    getCompanyEmployees();
  }, [page, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(debounceTerm), 1000)
    return () => clearTimeout(timer);
  }, [debounceTerm]);

  const fetchData = () => {
    if (page + 1 <= lastPage) {
      setPage(page + 1);
    }
  };

  const complaintsIRaised = complaintsData?.filter(
    (row: any) => row?.complain_by === isHRView?.emp_id
  );

  const complaintsAgainstMe = complaintsData?.filter(
    (row: any) => row?.complain_for === isHRView?.emp_id
  );

  const statusConfig: any = {
    1: { label: 'Approved', color: 'success' },
    0: { label: 'Pending', color: 'warning' },
    2: { label: 'Rejected', color: 'error' }
  };

  const handleDateChange = (newDate: any) => {
    const formattedDate = moment(newDate).format(
      defaultDateFormatting,
    );
    setDate(formattedDate);
  }

  const handleSubmit = () => {
    setIsLoading(true)
    const payload = {
      model: 'complaints',
      event: 'complaint',
      context: [
        {
          workflow_instance_id: 0,
          complain_by: isHRView?.emp_id,
          complain_for: selectedEmployee,
          date: date,
          title: subject,
          status: 0,
          description: description,
          company_id: isHRView?.company_id,
        },
      ],
      initiated_by: isHRView?.user_id
    };

    complaintService
      .submitApplicationHr(payload)
      .then((res) => {
        getComplaints();
        closeModal(false);
        setDate('');
        setSelectedEmployee(undefined);
        setSubject('');
        setDescription('');
        setIsLoading(false)
      })
      .then(() => {
        setIsLoading(false)
      })

  };

  return (
    <Grid
      container
      style={{
        display: 'flex',
        flexWrap: 'nowrap',
        width: '100vw',
        marginTop: isHRView ? '' : '4em',
        paddingRight: '8em',
      }}
    >
      <Grid
        item
        xs={11}
        sm={11}
        md={11}
        lg={11}
        sx={{
          backgroundColor: '#F5F5FA',
          width: '100%',
          padding: '1.75em',
          marginLeft:
            isHRView && i18n.language == 'en'
              ? '11em'
              : !isHRView && i18n.language == 'en'
                ? '14em'
                : '',
          marginRight: i18n.language == 'ar' ? '15em' : '',
        }}
      >
        <Grid
          container
          style={{
            paddingLeft: isHRView ? '2em' : '',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#F5F5FA',
              width: '100%',
              height: 'auto',

              marginRight: i18n?.language == 'ar' ? '6em' : '',
            }}
          >
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              sx={{
                width: '100%',
                height: '80px',
              }}
            >
              <Grid
                container
                style={{
                  width: 'auto',
                  background: '#FFFFFF 0% 0% no-repeat padding-box',
                  borderRadius: '10px',
                  opacity: '1',
                  display: 'flex',
                  flexDirection: 'row',
                  paddingTop: '1em',
                  paddingBottom: '1em',
                  paddingLeft: '1em',
                  paddingRight: '1em',
                }}
              >
                <Grid item xs={9}>
                  <Typography
                    style={{
                      fontFamily: fonts.fontFamily,
                      fontSize: fonts.header,
                      color: themes.blackDefault,
                      fontWeight: 'bold',
                    }}
                  >
                    {trans('Complaints')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid container>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
              >
                <Box>
                  <Grid container spacing={2}>
                    <Grid
                      item
                      xs={6}
                      sm={6}
                      md={6}
                      lg={6}
                      sx={{
                        // width: '72vw',
                      }}
                    >
                      {/* <Box
                        style={{
                          width: 'auto',
                          height: 'auto',
                          background: '#FFFFFF 0% 0% no-repeat padding-box',
                          borderRadius: '10px',
                          opacity: '1',
                          display: 'flex',
                          flexDirection: 'row',
                          paddingTop: '1em',
                          paddingBottom: '1em',
                          paddingLeft: '1em',
                          paddingRight: '1em',
                        }}
                      >
                        
                      </Box> */}
                      <Box
                        style={{
                          width: 'auto',
                          height: 'auto',
                          background: '#FFFFFF 0% 0% no-repeat padding-box',
                          borderRadius: '10px',
                          opacity: '1',
                          paddingTop: '1em',
                          paddingBottom: '1em',
                          paddingLeft: '1em',
                          paddingRight: '1em',
                          marginTop: '1em',
                        }}
                      >
                        <Typography
                          style={{
                            fontFamily: fonts.fontFamily,
                            fontSize: fonts.header,
                            color: themes.primary,
                            fontWeight: 'bold',
                          }}
                        >
                          {trans('Complaints Raised by Me')}
                        </Typography>
                        <Grid
                          container
                          style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexWrap: 'nowrap',
                            marginTop: '1em',
                            marginBottom: '1em',
                            marginLeft: '0.1em',
                            marginRight: '3em',
                          }}
                          spacing={2}
                        >
                          <CustomAddButton
                            btnLabel={trans('Add Complaints')}
                            onClick={() => closeModal(true)}
                          />
                        </Grid>
                        <Grid >
                          <Grid item>
                            <Box
                              className="ar_table"
                              sx={{
                                '& .header': {
                                  // backgroundColor: '#ffffffff',
                                  position: 'sticky',
                                },
                                // width: '40vw',
                                height: '56vh',
                                overflow: 'auto',
                                borderRadius: '0.8em',
                                border: '1px solid #0000001f',
                                padding: '1em'
                              }}
                            >
                              {complaintsIRaised.length > 0 ? _.map(complaintsIRaised, (row: any) => {
                                const status = statusConfig[row.status] || {
                                  label: 'Unknown',
                                  color: 'default'
                                };
                                return (
                                  <Paper
                                    key={row?.id}
                                    elevation={1}
                                    sx={{
                                      p: 2,
                                      mt: 2,
                                      mb: 2,
                                      borderRadius: 2,
                                      border: '1px solid #0000001f'
                                    }}
                                  >
                                    <Grid container spacing={2} alignItems="center">

                                      {/* Complain By */}
                                      <Grid item xs={12} sm={5}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                          Complain By
                                        </Typography>

                                        <Typography variant="body1" fontWeight={600}>
                                          {row.complainByEmployeeName}
                                        </Typography>

                                        <Typography variant="body2">
                                          {row.complainByDesignationName}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                          {row.complainByDepartmentName}
                                        </Typography>
                                      </Grid>

                                      {/* Arrow */}
                                      <Grid item xs={12} sm={2} textAlign="center">
                                        <Typography variant="h5">➡️</Typography>
                                      </Grid>

                                      {/* Complain For */}
                                      <Grid item xs={12} sm={5}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                          Complain For
                                        </Typography>

                                        <Typography variant="body1" fontWeight={600}>
                                          {row.complainForEmployeeName}
                                        </Typography>

                                        <Typography variant="body2">
                                          {row.complainForDesignationName}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                          {row.complainForDepartmentName}
                                        </Typography>
                                      </Grid>

                                      {/* Divider */}
                                      <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                      </Grid>

                                      {/* Common Footer */}
                                      <Grid item xs={12}>
                                        <Grid container spacing={2} alignItems="center">

                                          {/* Status */}
                                          <Grid item xs={12} sm={3}>
                                            <Typography variant="caption" color="text.secondary">
                                              Status
                                            </Typography>
                                            <Chip
                                              label={status.label}
                                              color={status.color}
                                              size="small"
                                            />
                                          </Grid>

                                          {/* Raised Date */}
                                          <Grid item xs={12} sm={3}>
                                            <Typography variant="caption" color="text.secondary">
                                              Raised Date
                                            </Typography>
                                            <Typography variant="body2">
                                              {moment(row.date).format('DD MMM YYYY')}
                                            </Typography>
                                          </Grid>

                                          {/* Description */}
                                          <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">
                                              Description
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                              }}
                                            >
                                              {row.description || '—'}
                                            </Typography>
                                          </Grid>

                                        </Grid>
                                      </Grid>

                                    </Grid>
                                  </Paper>

                                )
                              }) : (
                                <Box
                                  sx={{
                                    height: '100%',
                                    minHeight: 200,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'text.secondary',
                                    textAlign: 'center'
                                  }}
                                >
                                  <Typography variant="body2">
                                    No complaints found
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sm={6}
                      md={6}
                      lg={6}
                      sx={{
                        // width: '72vw',
                      }}
                    >
                      {/* <Box
                        style={{
                          width: 'auto',
                          height: 'auto',
                          background: '#FFFFFF 0% 0% no-repeat padding-box',
                          borderRadius: '10px',
                          opacity: '1',
                          display: 'flex',
                          flexDirection: 'row',
                          paddingTop: '1em',
                          paddingBottom: '1em',
                          paddingLeft: '1em',
                          paddingRight: '1em',
                        }}
                      >
                        <Typography
                          style={{
                            fontFamily: fonts.fontFamily,
                            fontSize: fonts.header,
                            color: themes.blackDefault,
                            fontWeight: 'bold',
                          }}
                        >
                          {trans('Attendance Exception')}
                        </Typography>
                      </Box> */}
                      <Box
                        style={{
                          width: 'auto',
                          height: 'auto',
                          background: '#FFFFFF 0% 0% no-repeat padding-box',
                          borderRadius: '10px',
                          opacity: '1',
                          paddingTop: '1em',
                          paddingBottom: '1em',
                          paddingLeft: '1em',
                          paddingRight: '1em',
                          marginTop: '1em',
                        }}
                      >
                        <Typography
                          style={{
                            fontFamily: fonts.fontFamily,
                            fontSize: fonts.header,
                            color: themes.primary,
                            fontWeight: 'bold',
                          }}
                        >
                          {trans('Complaints Raised Against Me')}
                        </Typography>
                        <Grid sx={{ marginTop: '2em' }}>
                          <Grid item>
                            <Box
                              className="ar_table"
                              sx={{
                                '& .header': {
                                  // backgroundColor: '#ffffffff',
                                  position: 'sticky',
                                },
                                // width: '40vw',
                                height: '60vh',
                                overflow: 'auto',
                                borderRadius: '0.8em',
                                border: '1px solid #0000001f',
                                padding: '1em'

                              }}
                            >
                              {complaintsAgainstMe?.length > 0 ? _.map(complaintsAgainstMe, (row: any) => {
                                const status = statusConfig[row.status] || {
                                  label: 'Unknown',
                                  color: 'default'
                                };
                                return (
                                  <Paper
                                    key={row?.id}
                                    elevation={1}
                                    sx={{
                                      p: 2,
                                      mt: 2,
                                      mb: 2,
                                      borderRadius: 2,
                                      border: '1px solid #0000001f'
                                    }}
                                  >
                                    <Grid container spacing={2} alignItems="center">

                                      {/* Complain By */}
                                      <Grid item xs={12} sm={5}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                          Complain By
                                        </Typography>

                                        <Typography variant="body1" fontWeight={600}>
                                          {row.complainByEmployeeName}
                                        </Typography>

                                        <Typography variant="body2">
                                          {row.complainByDesignationName}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                          {row.complainByDepartmentName}
                                        </Typography>
                                      </Grid>

                                      {/* Arrow */}
                                      <Grid item xs={12} sm={2} textAlign="center">
                                        <Typography variant="h5">➡️</Typography>
                                      </Grid>

                                      {/* Complain For */}
                                      <Grid item xs={12} sm={5}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                          Complain For
                                        </Typography>

                                        <Typography variant="body1" fontWeight={600}>
                                          {row.complainForEmployeeName}
                                        </Typography>

                                        <Typography variant="body2">
                                          {row.complainForDesignationName}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                          {row.complainForDepartmentName}
                                        </Typography>
                                      </Grid>

                                      {/* Divider */}
                                      <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                      </Grid>

                                      {/* Common Footer */}
                                      <Grid item xs={12}>
                                        <Grid container spacing={2} alignItems="center">

                                          {/* Status */}
                                          <Grid item xs={12} sm={3}>
                                            <Typography variant="caption" color="text.secondary">
                                              Status
                                            </Typography>
                                            <Chip
                                              label={status.label}
                                              color={status.color}
                                              size="small"
                                            />
                                          </Grid>

                                          {/* Raised Date */}
                                          <Grid item xs={12} sm={3}>
                                            <Typography variant="caption" color="text.secondary">
                                              Raised Date
                                            </Typography>
                                            <Typography variant="body2">
                                              {moment(row.date).format('DD MMM YYYY')}
                                            </Typography>
                                          </Grid>

                                          {/* Description */}
                                          <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">
                                              Description
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                              }}
                                            >
                                              {row.description || '—'}
                                            </Typography>
                                          </Grid>

                                        </Grid>
                                      </Grid>

                                    </Grid>
                                  </Paper>

                                )
                              }) : (
                                <Box
                                  sx={{
                                    height: '100%',
                                    minHeight: 200,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'text.secondary',
                                    textAlign: 'center'
                                  }}
                                >
                                  <Typography variant="body2">
                                    No complaints found
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
      <CustomModal
        isModalOpen={openModal}
        modalHeight="755px"
        modalWidth="755px"
      >
        <ModalHeader
          modal={{ modal_title: trans('Add Complaints') }}
          closeModal={() => {
            closeModal(false);
            setDate('');
            setSelectedEmployee(undefined);
            setSubject('');
            setDescription('');
          }}
        />
        <Grid
          container
          style={{
            marginTop: '35px',
          }}
          spacing={2}
        >
          <Grid item xs={6} lg={6} md={6}>
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: '0.8em',
                border: '1px solid #0000001f',
                padding: '1em'
              }}
            >
              <Grid item xs={12} style={{ marginBottom: '1em' }}>
                <SearchComponent onChange={(v: any) => setSearchTerm(v)} />
              </Grid>
              <InfinityList
                dataLength={employeeData?.length}
                next={fetchData}
                height={'380px'}
                hasMore={employeeData?.length < count || load
                  ? true
                  : false}
                loader={<h4>{trans('Loading...')}</h4>}
                endMessage={<p>{employeeData?.length ? trans('No more items to load') : 'No Employee Found'} </p>}
              >
                {employeeData.length ? (
                  <FormControl>
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      defaultValue={employeeData[0]?.id}
                      value={
                        selectedEmployee
                          ? Number(selectedEmployee)
                          : null
                      }
                      name="radio-buttons-group2"
                      onChange={(event: any) => setSelectedEmployee(event.target.value)}
                    >
                      {_.map(employeeData, function (employee) {
                        return (
                          <FormControlLabel
                            sx={{
                              backgroundColor: '#fafafa',
                              padding: 1,
                            }}
                            value={employee.id}
                            control={<Radio size="small" style={styles.radio} />}
                            label={
                              <Employee
                                fname={employee.first_name}
                                lname={employee.last_name}
                                job={employee.emp_job_detail}
                                staffid={employee.staff_id}
                              />
                            }
                          />
                        );
                      })}
                    </RadioGroup>
                  </FormControl>
                ) : (
                  <Loader width={200} />
                )}
              </InfinityList>
              <Grid
                container
                style={{
                  marginTop: '0.5em',
                }}
              >
                <Grid item>
                  <Typography
                    style={{
                      color: '#8F95B2',

                      fontSize: '0.75em',
                    }}
                  >
                    {trans(
                      `Total Employees : {{count}}/{{total_count}}`,
                      {
                        count: employeeData.length,
                        total_count: count,
                      },
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={6} lg={6} md={6} style={{ display: 'flex', flexDirection: 'column', alignItems: 'end'}}>
            <Grid item xs={12} lg={12} md={12}>
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '0.8em',
                  border: '1px solid #0000001f',
                  padding: '1em',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <CustomTextField
                  type="string"
                  multiline={true}
                  label={trans('Remarks')}
                  placeholder={trans('Subject')}
                  value={subject}
                  onChange={(e: any) => setSubject(e.target.value)}
                />
                <DatePickerInput
                  width={'335px'}
                  defaultValue={date}
                  value={date}
                  supportUTC={false}
                  onChange={handleDateChange}
                />
                <CustomTextField
                  type="string"
                  multiline={true}
                  label={trans('Remarks')}
                  placeholder={trans('Description')}
                  value={description}
                  onChange={(e: any) => setDescription(e.target.value)}
                />
              </Box>
            </Grid>
            <Grid item xs={12} lg={12} md={12}>
              <ModalFooter
                onClickSubmit={() => handleSubmit()}
                onClickCancel={() => {
                  closeModal(false);
                  setDate('');
                  setSelectedEmployee(undefined);
                  setSubject('');
                  setDescription('');
                }}
              // isSubmitButton={false}
              />
            </Grid>
          </Grid>
        </Grid>
        <Box style={{ marginTop: '35px' }} />
      </CustomModal >
    </Grid >
  );
};

export default ComplaintsPresenter;
